import { useState } from "react";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { SubmitButton } from "@/components/utils/SubmitButton";
import { FormsPropTypes, SelectedFormPropTypes } from "./propTypes";
import clsx from "clsx";
import "./styles.scss";
import { useNavigate } from "react-router-dom";

export const DEFAULT_FORM_ID = 1;
const DEFAULT_URL = "http://nicasource.com";

const Icon = () => (
  <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6.66641 5.2668L13.7414 2.90846C16.9164 1.85013 18.6414 3.58346 17.5914 6.75846L15.2331 13.8335C13.6497 18.5918 11.0497 18.5918 9.46641 13.8335L8.76641 11.7335L6.66641 11.0335C1.90807 9.45013 1.90807 6.85846 6.66641 5.2668Z"
      stroke="#F6FCFF"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8.92578 11.3745L11.9091 8.38281"
      stroke="#F6FCFF"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SelectedForm = ({ formId, setMessage }) => {
  const { url = DEFAULT_URL } = window.__REACT_WORDPRESS__.routing;

  const { executeRecaptcha } = useGoogleReCaptcha();

  const navigate = useNavigate();

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!executeRecaptcha) {
      setMessage("reCAPTCHA not yet available. Please try again later");
      return;
    }

    // Create a FormData object from the form
    const form = event.target;

    // Execute reCAPTCHA and get the token
    const token = await executeRecaptcha("forms");

    if (!token) {
      setMessage("reCAPTCHA verification failed, please try again");
      return;
    }

    submitForm(form, token);
  };

  //Selected services
  const [selectedServices, setSelectedServices] = useState([]);

  const services = [
    "Project Management",
    "QA Testing",
    "Salesforce Administration",
    "Salesforce Development",
    "Software Development",
    "UX/UI Design",
  ];

  // File upload states and handler - commented out for now as per request

  // const [fileBase64, setFileBase64] = useState("");
  // const [fileName, setFileName] = useState("");

  // const handleFileChange = (e) => {
  //   const file = e.target.files[0];
  //   if (file) {
  //     setFileName(file.name);

  //     const reader = new FileReader();
  //     reader.readAsDataURL(file);
  //     reader.onload = () => {
  //       const base64 = reader.result.split(",")[1];
  //       setFileBase64(base64);
  //     };
  //     reader.onerror = (error) => {
  //       console.error("Error to read file:", error);
  //     };
  //   }
  // };

  const toggleService = (service) => {
    setSelectedServices((prev) =>
      prev.includes(service) ? prev.filter((item) => item !== service) : [...prev, service]
    );
  };

  const submitForm = async (form, token) => {
    try {
      const formData = new FormData(form);

      // Append the reCAPTCHA token to the form data
      formData.append("token", token);

      const response = await fetch(`${url}/wp-admin/admin-post.php`, {
        method: "POST",
        body: formData,
      });

      const result = await response.json();
      const {
        data: { message = "" },
      } = result || {};

      if (response.ok) {
        form.reset();
        return navigate("/thank-you");
      } else {
        setMessage(message);
      }
    } catch (error) {
      setMessage("An error occurred. Please try again later");
    }

    // Reset the form
    document.querySelector(".sf-contact-form").reset();

    // scroll to bottom of page
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  };

  switch (formId) {
    // New Project
    case 1:
      return (
        <form className="sf-contact-form" onSubmit={handleSubmit}>
          <div className="input-container hidden">
            <input type="hidden" name="action" value="submit_form" />
            <input type="hidden" name="retURL" value={url} />
            <input type="hidden" name="type" value="new_project" />
          </div>

          <div className="input-container name-field">
            <label htmlFor="first_name">Full Name</label>
            <input id="first_name" maxLength="40" name="first_name" type="text" required />
          </div>

          <div className="input-container email-field">
            <label htmlFor="email">Email</label>
            <input id="email" maxLength="80" name="email" type="text" required />
          </div>

          <div className="input-container website-field">
            <label htmlFor="url">Website</label>
            <input id="url" maxLength="80" name="url" type="text" />
          </div>

          <div className="input-large-container services-message-container w-full">
            <div className="input-container services-field">
              <label htmlFor="00NPg000005JFTV">Service Requested</label>
              <div className="services-select flex flex-wrap gap-2 mt-2">
                {services.map((service) => (
                  <div
                    key={service}
                    onClick={() => toggleService(service)}
                    className={clsx(
                      "cursor-pointer px-3 py-1 rounded-full border flex items-center gap-2 transition-colors duration-200",
                      selectedServices.includes(service)
                        ? "!bg-watermelon !border-watermelon"
                        : "bg-transparent border-white"
                    )}
                  >
                    <span>{service}</span>

                    {selectedServices.includes(service) && (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="#F6FCFF"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="ml-1"
                      >
                        <circle cx="8" cy="8" r="6.667" />
                        <path d="M6.11 9.89L9.89 6.11" />
                        <path d="M9.89 9.89L6.11 6.11" />
                      </svg>
                    )}
                  </div>
                ))}
              </div>

              <input
                type="hidden"
                id="00NPg000005JFTV"
                name="00NPg000005JFTV"
                value={selectedServices.join(";")}
              />
            </div>

            <div className="flex flex-col justify-end w-full">
              <div className="input-container message-field last-field">
                <label htmlFor="00N3t00000Mnjbl">Message</label>
                <textarea
                  id="00N3t00000Mnjbl"
                  name="00N3t00000Mnjbl"
                  type="text"
                  wrap="soft"
                  required
                />
              </div>

              <div className="form-button-container">
                <SubmitButton
                  className="flex flex-row items-center justify-center p-2 gap-2 button-small h-10 text-light rounded-lg border border-light hover:border-watermelon"
                  isSubmit
                  name="Send"
                  showLabel={true}
                  onlyIcon
                  Icon={Icon}
                />
              </div>
            </div>
          </div>
        </form>
      );

    // Join Our Team
    case 2:
      return (
        <form className="sf-contact-form" onSubmit={handleSubmit}>
          <div className="input-container hidden">
            <input type="hidden" name="action" value="submit_form" />
            <input type="hidden" name="retURL" value={url} />
            <input type="hidden" name="type" value="new_hire" />
          </div>

          <div className="input-container">
            <label htmlFor="first_name">Full Name</label>
            <input id="first_name" maxLength="40" name="first_name" type="text" required />
          </div>

          <div className="input-container">
            <label htmlFor="email">Email</label>
            <input id="email" maxLength="80" name="email" type="text" required />
          </div>

          <div className="input-container">
            <label htmlFor="phone">Phone</label>
            <input id="phone" maxLength="40" name="phone" type="text" />
          </div>

          <div className="input-container country-field">
            <label htmlFor="country_code">Country</label>
            <select id="country_code" name="country_code" title="Country">
              <option value="">Country</option>
              <option value="AF">Afghanistan</option>
              <option value="AX">Åland Islands</option>
              <option value="AL">Albania</option>
              <option value="DZ">Algeria</option>
              <option value="AD">Andorra</option>
              <option value="AO">Angola</option>
              <option value="AI">Anguilla</option>
              <option value="AQ">Antarctica</option>
              <option value="AG">Antigua and Barbuda</option>
              <option value="AR">Argentina</option>
              <option value="AM">Armenia</option>
              <option value="AW">Aruba</option>
              <option value="AU">Australia</option>
              <option value="AT">Austria</option>
              <option value="AZ">Azerbaijan</option>
              <option value="BS">Bahamas</option>
              <option value="BH">Bahrain</option>
              <option value="BD">Bangladesh</option>
              <option value="BB">Barbados</option>
              <option value="BY">Belarus</option>
              <option value="BE">Belgium</option>
              <option value="BZ">Belize</option>
              <option value="BJ">Benin</option>
              <option value="BM">Bermuda</option>
              <option value="BT">Bhutan</option>
              <option value="BO">Bolivia</option>
              <option value="BQ">Bonaire, Sint Eustatius and Saba</option>
              <option value="BA">Bosnia and Herzegovina</option>
              <option value="BW">Botswana</option>
              <option value="BV">Bouvet Island</option>
              <option value="BR">Brazil</option>
              <option value="IO">British Indian Ocean Territory</option>
              <option value="BN">Brunei Darussalam</option>
              <option value="BG">Bulgaria</option>
              <option value="BF">Burkina Faso</option>
              <option value="BI">Burundi</option>
              <option value="KH">Cambodia</option>
              <option value="CM">Cameroon</option>
              <option value="CA">Canada</option>
              <option value="CV">Cape Verde</option>
              <option value="KY">Cayman Islands</option>
              <option value="CF">Central African Republic</option>
              <option value="TD">Chad</option>
              <option value="CL">Chile</option>
              <option value="CN">China</option>
              <option value="TW">Taiwan</option>
              <option value="CX">Christmas Island</option>
              <option value="CC">Cocos (Keeling) Islands</option>
              <option value="CO">Colombia</option>
              <option value="KM">Comoros</option>
              <option value="CG">Congo</option>
              <option value="CD">Congo, the Democratic Republic of the</option>
              <option value="CK">Cook Islands</option>
              <option value="CR">Costa Rica</option>
              <option value="CI">Côte d&apos;Ivoire</option>
              <option value="HR">Croatia</option>
              <option value="CU">Cuba</option>
              <option value="CW">Curaçao</option>
              <option value="CY">Cyprus</option>
              <option value="CZ">Czech Republic</option>
              <option value="DK">Denmark</option>
              <option value="DJ">Djibouti</option>
              <option value="DM">Dominica</option>
              <option value="DO">Dominican Republic</option>
              <option value="EC">Ecuador</option>
              <option value="EG">Egypt</option>
              <option value="SV">El Salvador</option>
              <option value="GQ">Equatorial Guinea</option>
              <option value="ER">Eritrea</option>
              <option value="EE">Estonia</option>
              <option value="ET">Ethiopia</option>
              <option value="FK">Falkland Islands (Malvinas)</option>
              <option value="FO">Faroe Islands</option>
              <option value="FJ">Fiji</option>
              <option value="FI">Finland</option>
              <option value="FR">France</option>
              <option value="GF">French Guiana</option>
              <option value="PF">French Polynesia</option>
              <option value="TF">French Southern Territories</option>
              <option value="GA">Gabon</option>
              <option value="GM">Gambia</option>
              <option value="GE">Georgia</option>
              <option value="DE">Germany</option>
              <option value="GH">Ghana</option>
              <option value="GI">Gibraltar</option>
              <option value="GR">Greece</option>
              <option value="GL">Greenland</option>
              <option value="GD">Grenada</option>
              <option value="GP">Guadeloupe</option>
              <option value="GT">Guatemala</option>
              <option value="GG">Guernsey</option>
              <option value="GN">Guinea</option>
              <option value="GW">Guinea-Bissau</option>
              <option value="GY">Guyana</option>
              <option value="HT">Haiti</option>
              <option value="HM">Heard Island and McDonald Islands</option>
              <option value="VA">Holy See (Vatican City State)</option>
              <option value="HN">Honduras</option>
              <option value="HU">Hungary</option>
              <option value="IS">Iceland</option>
              <option value="IN">India</option>
              <option value="ID">Indonesia</option>
              <option value="IR">Iran</option>
              <option value="IQ">Iraq</option>
              <option value="IE">Ireland</option>
              <option value="IM">Isle of Man</option>
              <option value="IL">Israel</option>
              <option value="IT">Italy</option>
              <option value="JM">Jamaica</option>
              <option value="JP">Japan</option>
              <option value="JE">Jersey</option>
              <option value="JO">Jordan</option>
              <option value="KZ">Kazakhstan</option>
              <option value="KE">Kenya</option>
              <option value="KI">Kiribati</option>
              <option value="KP">Korea, Democratic People&apos;s Republic of</option>
              <option value="KR">Korea, Republic of</option>
              <option value="KW">Kuwait</option>
              <option value="KG">Kyrgyzstan</option>
              <option value="LA">Lao People&apos;s Democratic Republic</option>
              <option value="LV">Latvia</option>
              <option value="LB">Lebanon</option>
              <option value="LS">Lesotho</option>
              <option value="LR">Liberia</option>
              <option value="LY">Libya</option>
              <option value="LI">Liechtenstein</option>
              <option value="LT">Lithuania</option>
              <option value="LU">Luxembourg</option>
              <option value="MO">Macao</option>
              <option value="MK">North Macedonia</option>
              <option value="MG">Madagascar</option>
              <option value="MW">Malawi</option>
              <option value="MY">Malaysia</option>
              <option value="MV">Maldives</option>
              <option value="ML">Mali</option>
              <option value="MT">Malta</option>
              <option value="MQ">Martinique</option>
              <option value="MR">Mauritania</option>
              <option value="MU">Mauritius</option>
              <option value="YT">Mayotte</option>
              <option value="MX">Mexico</option>
              <option value="MD">Moldova</option>
              <option value="MC">Monaco</option>
              <option value="MN">Mongolia</option>
              <option value="ME">Montenegro</option>
              <option value="MS">Montserrat</option>
              <option value="MA">Morocco</option>
              <option value="MZ">Mozambique</option>
              <option value="MM">Myanmar</option>
              <option value="NA">Namibia</option>
              <option value="NR">Nauru</option>
              <option value="NP">Nepal</option>
              <option value="NL">Netherlands</option>
              <option value="NC">New Caledonia</option>
              <option value="NZ">New Zealand</option>
              <option value="NI">Nicaragua</option>
              <option value="NE">Niger</option>
              <option value="NG">Nigeria</option>
              <option value="NU">Niue</option>
              <option value="NF">Norfolk Island</option>
              <option value="NO">Norway</option>
              <option value="OM">Oman</option>
              <option value="PK">Pakistan</option>
              <option value="PS">Palestine, State of</option>
              <option value="PA">Panama</option>
              <option value="PG">Papua New Guinea</option>
              <option value="PY">Paraguay</option>
              <option value="PE">Peru</option>
              <option value="PH">Philippines</option>
              <option value="PN">Pitcairn</option>
              <option value="PL">Poland</option>
              <option value="PT">Portugal</option>
              <option value="QA">Qatar</option>
              <option value="RE">Réunion</option>
              <option value="RO">Romania</option>
              <option value="RU">Russian Federation</option>
              <option value="RW">Rwanda</option>
              <option value="BL">Saint Barthélemy</option>
              <option value="SH">Saint Helena, Ascension and Tristan da Cunha</option>
              <option value="KN">Saint Kitts and Nevis</option>
              <option value="LC">Saint Lucia</option>
              <option value="MF">Saint Martin (French part)</option>
              <option value="PM">Saint Pierre and Miquelon</option>
              <option value="VC">Saint Vincent and the Grenadines</option>
              <option value="WS">Samoa</option>
              <option value="SM">San Marino</option>
              <option value="ST">Sao Tome and Principe</option>
              <option value="SA">Saudi Arabia</option>
              <option value="SN">Senegal</option>
              <option value="RS">Serbia</option>
              <option value="SC">Seychelles</option>
              <option value="SL">Sierra Leone</option>
              <option value="SG">Singapore</option>
              <option value="SX">Sint Maarten (Dutch part)</option>
              <option value="SK">Slovakia</option>
              <option value="SI">Slovenia</option>
              <option value="SB">Solomon Islands</option>
              <option value="SO">Somalia</option>
              <option value="ZA">South Africa</option>
              <option value="GS">South Georgia and the South Sandwich Islands</option>
              <option value="SS">South Sudan</option>
              <option value="ES">Spain</option>
              <option value="LK">Sri Lanka</option>
              <option value="SD">Sudan</option>
              <option value="SR">Suriname</option>
              <option value="SJ">Svalbard and Jan Mayen</option>
              <option value="SZ">Eswatini</option>
              <option value="SE">Sweden</option>
              <option value="CH">Switzerland</option>
              <option value="SY">Syrian Arab Republic</option>
              <option value="TJ">Tajikistan</option>
              <option value="TZ">Tanzania, United Republic of</option>
              <option value="TH">Thailand</option>
              <option value="TL">Timor-Leste</option>
              <option value="TG">Togo</option>
              <option value="TK">Tokelau</option>
              <option value="TO">Tonga</option>
              <option value="TT">Trinidad and Tobago</option>
              <option value="TN">Tunisia</option>
              <option value="TR">Turkey</option>
              <option value="TM">Turkmenistan</option>
              <option value="TC">Turks and Caicos Islands</option>
              <option value="TV">Tuvalu</option>
              <option value="UG">Uganda</option>
              <option value="UA">Ukraine</option>
              <option value="AE">United Arab Emirates</option>
              <option value="GB">United Kingdom</option>
              <option value="US">United States</option>
              <option value="UY">Uruguay</option>
              <option value="UZ">Uzbekistan</option>
              <option value="VU">Vanuatu</option>
              <option value="VE">Venezuela</option>
              <option value="VN">Viet Nam</option>
              <option value="VG">Virgin Islands, British</option>
              <option value="WF">Wallis and Futuna</option>
              <option value="EH">Western Sahara</option>
              <option value="YE">Yemen</option>
              <option value="ZM">Zambia</option>
              <option value="ZW">Zimbabwe</option>
            </select>
          </div>

          {/* Handle file upload - commented out for now as per request */}
          {/* <div className="input-container portfolio-field">
            <label htmlFor="portfolio" className="font-bold block mb-2">
              File
            </label>

            <div className="custom-file-wrapper">
              <span className="file-name">{fileName || "Select a file"}</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M9.20117 17V11L7.20117 13"
                  stroke="#F6FCFF"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9.20117 11L11.2012 13"
                  stroke="#F6FCFF"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M22.2012 10V15C22.2012 20 20.2012 22 15.2012 22H9.20117C4.20117 22 2.20117 20 2.20117 15V9C2.20117 4 4.20117 2 9.20117 2H14.2012"
                  stroke="#F6FCFF"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M22.2012 10H18.2012C15.2012 10 14.2012 9 14.2012 6V2L22.2012 10Z"
                  stroke="#F6FCFF"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <input
                id="portfolio"
                name="portfolio"
                type="file"
                accept=".pdf,.doc,.docx,.txt"
                onChange={(e) => {
                  handleFileChange(e);
                  setFileName(e.target.files[0]?.name || "");
                }}
              />
            </div>
          </div> */}

          <div className="flex flex-col justify-end w-full">
            <div className="input-container last-field">
              <label htmlFor="00N3t00000Mnjbl">Message</label>
              <textarea id="00N3t00000Mnjbl" name="00N3t00000Mnjbl" type="text" wrap="soft" />
            </div>

            <div className="form-button-container">
              <SubmitButton
                className="flex flex-row items-center justify-center p-2 gap-2 button-small h-10 text-light rounded-lg border border-light hover:border-watermelon"
                isSubmit
                name="Apply Now"
                showLabel={true}
                onlyIcon
                Icon={Icon}
              />
            </div>
          </div>
        </form>
      );

    // General Inquiries
    case 3:
      return (
        <form className="sf-contact-form" onSubmit={handleSubmit}>
          <div className="input-container hidden">
            <input type="hidden" name="action" value="submit_form" />
            <input type="hidden" name="retURL" value={url} />
            <input type="hidden" name="type" value="general_inquiries" />
          </div>

          <div className="input-container name_inquiries_field">
            <label htmlFor="first_name">Full Name</label>
            <input id="first_name" maxLength="40" name="first_name" type="text" required />
          </div>

          <div className="input-container company-field">
            <label htmlFor="company">Company</label>
            <input id="company" maxLength="40" name="company" type="text" />
          </div>

          <div className="input-container email_inquiries_field">
            <label htmlFor="email">Email</label>
            <input id="email" maxLength="80" name="email" type="text" required />
          </div>

          <div className="flex flex-col justify-end w-full">
            <div className="input-container last-field">
              <label htmlFor="00N3t00000Mnjbl">Message</label>
              <textarea
                id="00N3t00000Mnjbl"
                name="00N3t00000Mnjbl"
                type="text"
                wrap="soft"
                required
              />
            </div>

            <div className="form-button-container">
              <SubmitButton
                className="flex flex-row items-center justify-center p-2 gap-2 button-small h-10 text-light rounded-lg border border-light hover:border-watermelon"
                isSubmit
                name="Send"
                showLabel={true}
                onlyIcon
                Icon={Icon}
              />
            </div>
          </div>
        </form>
      );

    default:
      return null;
  }
};

SelectedForm.propTypes = SelectedFormPropTypes;

const Forms = ({ formId }) => {
  const [message, setMessage] = useState("");

  return (
    <GoogleReCaptchaProvider reCaptchaKey="6LdNRHEcAAAAABHu_D1ADKXL92ZEBkcvIa0-iKEb">
      <SelectedForm formId={formId} setMessage={setMessage} />

      {message && <p className="sf-contact-form-message">{message}</p>}
    </GoogleReCaptchaProvider>
  );
};

Forms.propTypes = FormsPropTypes;

export default Forms;
